/**
 * Reading and writing the submission queue.
 *
 * Deliberately dull. Every decision about *whether* to retry, *when*, and *when to
 * stop lives in core/outbox.ts where it is pure and tested; this file only moves rows
 * in and out of IndexedDB.
 *
 * One rule runs through all of it: a row is never deleted because delivery failed.
 * The only thing that removes a record is a successful send, and even then a receipt
 * is written in its place.
 */

import { afterFailure, afterSuccess, isDue, isRetryableStatus } from '@tes-inspect/core/outbox';
import type { DeliveryState } from '@tes-inspect/core/outbox';
import { SUBMISSION_BACKOFF } from '@tes-inspect/core/retry';

import type {
  InspectDb,
  OutboxRecord,
  PhotoRecord,
  Receipt,
} from './idb-schema.ts';

export interface FailureReport {
  readonly reason: string;
  /** Absent when the request never reached a server, which is the usual case. */
  readonly httpStatus?: number;
}

export interface RetryClock {
  readonly nowMs: number;
  /** A value in [0, 1] for backoff jitter, supplied so this stays deterministic in tests. */
  readonly randomFraction: number;
}

export async function enqueue(db: InspectDb, record: OutboxRecord): Promise<void> {
  await db.put('outbox', record);
}

/**
 * Rows a sender should attempt right now.
 *
 * Filtering happens in memory rather than through an index range because the queue is
 * a handful of rows on a phone, and one predicate -- `isDue` -- shared with the server
 * is worth more than a query optimisation nobody will ever measure.
 */
export async function dueSubmissions(db: InspectDb, nowMs: number): Promise<OutboxRecord[]> {
  const all = await db.getAll('outbox');
  return all.filter((record) => isDue(record, nowMs));
}

export async function pendingCount(db: InspectDb): Promise<number> {
  const all = await db.getAll('outbox');
  return all.filter((record) => record.status === 'queued' || record.status === 'sending').length;
}

/** Rows that stopped for a reason a person has to resolve. */
export async function failedSubmissions(db: InspectDb): Promise<OutboxRecord[]> {
  const all = await db.getAll('outbox');
  return all.filter((record) => record.status === 'failed' || record.status === 'dead');
}

export async function markSending(db: InspectDb, id: string): Promise<void> {
  await patchDelivery(db, id, (record) => ({ ...record, status: 'sending' }));
}

/**
 * Replaces the queued row with a receipt.
 *
 * The payload goes away because it is now the server's; the receipt stays so the
 * operator's queue badge can show what happened, and so a duplicate scan can tell
 * that this submission already landed.
 */
export async function markSent(db: InspectDb, id: string, receipt: Receipt): Promise<void> {
  const record = await db.get('outbox', id);

  if (record === undefined) {
    return;
  }

  const transaction = db.transaction(['outbox', 'receipts'], 'readwrite');
  await Promise.all([
    transaction.objectStore('outbox').put({ ...record, ...afterSuccess(record) }),
    transaction.objectStore('receipts').put(receipt),
    transaction.done,
  ]);
}

export async function markFailed(
  db: InspectDb,
  id: string,
  failure: FailureReport & RetryClock,
): Promise<void> {
  await patchDelivery(db, id, (record) =>
    afterFailure({
      state: record,
      schedule: SUBMISSION_BACKOFF,
      reason: failure.reason,
      retryable: isRetryableStatus(failure.httpStatus),
      nowMs: failure.nowMs,
      randomFraction: failure.randomFraction,
    }),
  );
}

export async function putPhoto(db: InspectDb, photo: PhotoRecord): Promise<void> {
  await db.put('photos', photo);
}

export async function photosFor(db: InspectDb, outboxId: string): Promise<PhotoRecord[]> {
  return db.getAllFromIndex('photos', 'by-outbox', outboxId);
}

export async function pendingPhotos(db: InspectDb): Promise<PhotoRecord[]> {
  return db.getAllFromIndex('photos', 'by-upload-status', 'pending');
}

export async function markPhotoUploaded(db: InspectDb, photoId: string): Promise<void> {
  const photo = await db.get('photos', photoId);

  if (photo !== undefined) {
    await db.put('photos', { ...photo, uploadStatus: 'uploaded' });
  }
}

async function patchDelivery(
  db: InspectDb,
  id: string,
  next: (record: OutboxRecord) => DeliveryState,
): Promise<void> {
  const record = await db.get('outbox', id);

  if (record === undefined) {
    return;
  }

  await db.put('outbox', { ...record, ...next(record) });
}
